import { BuildingsIcon, UserIcon } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FrontLayout from 'src/components/layouts/FrontLayout';
import TitledPanel from 'src/components/ui/TitledPanel';
import { prefixWithTenant } from 'src/lib/tenantRouting';

interface PickerTenant {
  code: string;
  name: string;
  square_logo_url?: string | null;
  role?: string | null;
  tenant_type?: string | null;
}

function TenantAvatar({ name, logoUrl, isPersonal }: { name: string; logoUrl?: string | null; isPersonal: boolean }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="h-9 w-9 shrink-0 rounded-lg object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-raised flex-shrink-0">
      {isPersonal ? (
        <UserIcon size={18} className="text-text-secondary" />
      ) : (
        <BuildingsIcon size={18} className="text-text-secondary" />
      )}
    </div>
  );
}

interface TenantPickerPageProps {
  tenants: PickerTenant[];
  /** Path to open after a choice, including any search string and hash. */
  originalPath: string;
  title?: string;
  description?: string;
}

export default function TenantPickerPage({
  tenants,
  originalPath,
  title = 'Choose an organisation',
  description = "Select which organisation you'd like to open this link in."
}: TenantPickerPageProps) {
  const navigate = useNavigate();

  const handlePick = (code: string) => {
    navigate(prefixWithTenant(originalPath, code), { replace: true });
  };

  return (
    <FrontLayout>
      <TitledPanel
        title={<h1 className="text-lg font-semibold text-content-default">{title}</h1>}
        subtitle={description}
        className="gap-4"
      >
        <div className="flex flex-col gap-2">
          {[...tenants]
            .sort((a, b) => {
              const aGuest = a.role === 'guest' ? 1 : 0;
              const bGuest = b.role === 'guest' ? 1 : 0;
              return aGuest - bGuest;
            })
            .map((tenant) => {
              const isPersonal = tenant.tenant_type === 'personal';
              const displayName = isPersonal ? 'Personal' : tenant.name;
              return (
                <button
                  key={tenant.code}
                  onClick={() => handlePick(tenant.code)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border-default hover:bg-surface-hover text-left transition-colors w-full"
                >
                  <TenantAvatar name={tenant.name} logoUrl={tenant.square_logo_url} isPersonal={isPersonal} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content-default truncate">{displayName}</p>
                    {tenant.role && <p className="text-xs text-content-subtlest capitalize">{tenant.role}</p>}
                  </div>
                </button>
              );
            })}
        </div>
      </TitledPanel>
    </FrontLayout>
  );
}
